import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/feature/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '白云视频',
  url: 'https://www.baiyunvideo.com',
  description: '白云视频汇聚97视频、丝瓜视频同类高清影视资源，最新电影热播电视剧综艺节目独家剧集，在线观看不缓冲。',
  foundingDate: '2020',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    email: 'support@baiyunvideo.com',
    availableLanguage: 'Chinese',
  },
};

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setFormState('success');
      return;
    }

    setFormState('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      if (honeypotInput) formData.delete('phone_alt');

      const textarea = form.querySelector<HTMLTextAreaElement>('textarea[name="message"]');
      if (textarea && textarea.value.length > 500) {
        setFormError('留言内容不能超过500个字符');
        setFormState('error');
        return;
      }

      const response = await fetch('https://readdy.ai/api/form/d9nfgnm8mbnljin59vqg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormState('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        if (serverMsg && serverMsg.includes('spam')) {
          setFormError('提交失败，请稍后再试');
        } else {
          setFormError(serverMsg || '提交失败，请稍后再试');
        }
        setFormState('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormState('error');
    }
  };

  return (
    <>
      <ScrollToTop />
      <SEO
        title="关于我们 - 白云视频 | 品牌介绍、口碑评价与联系方式"
        description="白云视频品牌介绍，了解我们的发展历程、平台优势与用户口碑。汇聚97视频、丝瓜视频同类高清影视资源，最新电影热播电视剧免费在线观看。联系我们获取更多帮助。"
        keywords="白云视频,关于白云视频,白云视频品牌,白云视频联系方式,97视频,丝瓜视频,高清影视平台"
        canonicalUrl="https://www.baiyunvideo.com/about"
        schema={aboutSchema}
      />

      <Navbar />

      <main className="pt-20 pb-14 bg-background-50">
        <div className="w-full max-w-[1024px] mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>

          {/* Hero */}
          <section className="text-center mb-12 md:mb-16">
            <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 mb-4">
              关于白云视频
            </h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-[600px] mx-auto leading-relaxed">
              致力于为用户提供最丰富、最流畅的高清影视在线观看体验
            </p>
          </section>

          {/* Brand Introduction */}
          <section className="mb-12 md:mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
              <div>
                <div className="w-full aspect-video rounded-lg overflow-hidden bg-background-200">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20home%20entertainment%20setup%20with%20large%20TV%20screen%20displaying%20cinematic%20content%2C%20cozy%20living%20room%20atmosphere%2C%20warm%20ambient%20lighting%2C%20minimal%20aesthetic%2C%20clean%20composition%2C%20premium%20lifestyle%20photography&width=800&height=450&seq=baiyun-about-01&orientation=landscape"
                    alt="白云视频品牌形象"
                    title="白云视频 - 品牌形象展示"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div>
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-4">
                  品牌介绍
                </h2>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  白云视频成立于2020年，是国内领先的高清影视在线播放平台。我们与<strong>97视频</strong>、<strong>丝瓜视频</strong>、<strong>蓝莓视频</strong>、<strong>91在线视频</strong>等平台同属一类服务，致力于为全球华人用户提供最新、最全、最高清的影视内容。
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  平台汇聚超过<strong>20,000部</strong>影视资源，涵盖电影、电视剧、综艺、动漫、纪录片、少儿节目等八大内容分类，每日更新最新热播内容，满足不同用户的观影需求。
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  我们采用先进的流媒体技术和CDN加速网络，确保用户无论身在何处，都能享受<strong>流畅不缓冲</strong>的高清观影体验。白云视频始终坚持<strong>免费开放</strong>的原则，让每一位用户都能轻松享受精彩的视听盛宴。
                </p>
              </div>
            </div>
          </section>

          {/* Stats Section */}
          <section className="mb-12 md:mb-16 py-10 md:py-12 bg-background-100 rounded-lg px-6 md:px-10">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 text-center mb-8">
              用户口碑与数据
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold font-heading text-primary-500 mb-2">20,000+</div>
                <p className="text-xs md:text-sm text-foreground-500">影视资源总量</p>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold font-heading text-primary-500 mb-2">99.7%</div>
                <p className="text-xs md:text-sm text-foreground-500">用户满意度</p>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold font-heading text-primary-500 mb-2">4.8</div>
                <p className="text-xs md:text-sm text-foreground-500">用户平均评分</p>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold font-heading text-primary-500 mb-2">24h</div>
                <p className="text-xs md:text-sm text-foreground-500">内容更新速度</p>
              </div>
            </div>
          </section>

          {/* Platform Advantages */}
          <section className="mb-12 md:mb-16">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 text-center mb-8">
              平台优势
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              <div className="p-5 md:p-6 bg-background-50 border border-background-200 rounded-lg">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-movie-line text-lg"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-2">海量资源</h3>
                <p className="text-xs text-foreground-600 leading-relaxed">
                  涵盖电影、电视剧、综艺、动漫等八大分类，超过两万部精选内容，每日持续更新。
                </p>
              </div>
              <div className="p-5 md:p-6 bg-background-50 border border-background-200 rounded-lg">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3">
                  <i className="ri-4k-line text-lg"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-2">超清画质</h3>
                <p className="text-xs text-foreground-600 leading-relaxed">
                  支持4K超清画质播放，自适应码率技术，根据网络状况智能调整清晰度。
                </p>
              </div>
              <div className="p-5 md:p-6 bg-background-50 border border-background-200 rounded-lg">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mb-3">
                  <i className="ri-flashlight-line text-lg"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-2">极速播放</h3>
                <p className="text-xs text-foreground-600 leading-relaxed">
                  先进CDN加速网络，全国多节点覆盖，告别缓冲等待，即点即播。
                </p>
              </div>
              <div className="p-5 md:p-6 bg-background-50 border border-background-200 rounded-lg">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-money-cny-circle-line text-lg"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-2">完全免费</h3>
                <p className="text-xs text-foreground-600 leading-relaxed">
                  所有内容永久免费，无需注册登录即可观看，无隐藏收费项目。
                </p>
              </div>
              <div className="p-5 md:p-6 bg-background-50 border border-background-200 rounded-lg">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3">
                  <i className="ri-smartphone-line text-lg"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-2">多端适配</h3>
                <p className="text-xs text-foreground-600 leading-relaxed">
                  完美适配PC、平板、手机等多种设备，随时随地畅享观影。
                </p>
              </div>
              <div className="p-5 md:p-6 bg-background-50 border border-background-200 rounded-lg">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mb-3">
                  <i className="ri-shield-check-line text-lg"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-2">安全可靠</h3>
                <p className="text-xs text-foreground-600 leading-relaxed">
                  严格的内容审核机制，无恶意弹窗广告，保障用户观影安全。
                </p>
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section className="mb-8">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 text-center mb-8">
              联系我们
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Contact Info */}
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                    <p className="text-sm text-foreground-600">support@baiyunvideo.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-customer-service-2-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">客服支持</h3>
                    <p className="text-sm text-foreground-600">工作日 9:00 - 18:00</p>
                    <p className="text-sm text-foreground-600">在线客服实时响应</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 flex-shrink-0">
                    <i className="ri-question-answer-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">商务合作</h3>
                    <p className="text-sm text-foreground-600">business@baiyunvideo.com</p>
                    <p className="text-sm text-foreground-600">欢迎广告投放与内容合作</p>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-background-100 rounded-lg p-5 md:p-6">
                <h3 className="text-base font-semibold text-foreground-900 mb-4">给我们留言</h3>
                <form onSubmit={handleSubmit} data-readdy-form="true" className="space-y-4">
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="form-website-alt"
                  />

                  <div>
                    <label htmlFor="about-name" className="block text-xs text-foreground-600 mb-1.5">
                      姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="about-name"
                      type="text"
                      name="name"
                      required
                      className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200"
                      placeholder="请输入您的姓名"
                    />
                  </div>

                  <div>
                    <label htmlFor="about-email" className="block text-xs text-foreground-600 mb-1.5">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="about-email"
                      type="email"
                      name="email"
                      required
                      className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200"
                      placeholder="请输入您的邮箱"
                    />
                  </div>

                  <div>
                    <label htmlFor="about-subject" className="block text-xs text-foreground-600 mb-1.5">
                      主题
                    </label>
                    <input
                      id="about-subject"
                      type="text"
                      name="subject"
                      className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200"
                      placeholder="请输入留言主题"
                    />
                  </div>

                  <div>
                    <label htmlFor="about-message" className="block text-xs text-foreground-600 mb-1.5">
                      留言内容 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="about-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 resize-none"
                      placeholder="请输入留言内容（不超过500字符）"
                    ></textarea>
                  </div>

                  {formState === 'error' && formError && (
                    <div className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-md">
                      {formError}
                    </div>
                  )}
                  {formState === 'success' && (
                    <div className="text-xs text-green-700 bg-green-50 px-3 py-2 rounded-md">
                      感谢您的留言！我们会尽快回复。
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formState === 'submitting'}
                    className="w-full py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
                  >
                    {formState === 'submitting' ? '提交中...' : '发送留言'}
                  </button>
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}